import { ClientInfo, DocumentRequest } from './types';

/**
 * Service class for audit document request management
 */
export class AuditService {
  private clients: ClientInfo[];
  private documentRequests: DocumentRequest[];

  constructor() {
    // Initialize with sample data
    this.clients = this.generateSampleClients();
    this.documentRequests = this.generateSampleDocumentRequests();
  }

  // Get all clients
  getClients(): ClientInfo[] {
    return this.clients;
  }

  // Get client by ID
  getClientById(clientId: string): ClientInfo | undefined {
    return this.clients.find(client => client.id === clientId);
  }

  // Get pending documents for a client
  getPendingDocuments(clientId: string): DocumentRequest[] {
    return this.documentRequests.filter(
      doc => doc.clientId === clientId && doc.status === 'pending'
    );
  }

  // Get sent documents for a client
  getSentDocuments(clientId: string): DocumentRequest[] {
    return this.documentRequests.filter(
      doc =>
        doc.clientId === clientId &&
        (doc.status === 'sent' || doc.status === 'confirmed')
    );
  }

  // Mark document as sent
  markDocumentAsSent(
    documentId: string,
    sentDate: string = new Date().toISOString()
  ): boolean {
    const docIndex = this.documentRequests.findIndex(
      doc => doc.id === documentId
    );
    if (docIndex === -1) return false;

    this.documentRequests[docIndex] = {
      ...this.documentRequests[docIndex],
      status: 'sent',
      sentDate
    };

    return true;
  }

  // Register estimated send date
  setEstimatedSendDate(documentId: string, estimatedDate: string): boolean {
    const docIndex = this.documentRequests.findIndex(
      doc => doc.id === documentId
    );
    if (docIndex === -1) return false;

    this.documentRequests[docIndex] = {
      ...this.documentRequests[docIndex],
      notes: `Cliente enviará aproximadamente el ${estimatedDate}`
    };

    return true;
  }

  // Generate 15 sample clients
  private generateSampleClients(): ClientInfo[] {
    return [
      {
        id: 'client-1',
        name: 'Carlos Rodríguez',
        company: 'Tecnologías Innovadoras S.L.',
        companyInitials: 'TI',
        email: 'carlos@tecno-innova.com',
        phoneNumber: '+34 612 345 678',
        assignedAuditor: 'Marina Torres'
      },
      {
        id: 'client-2',
        name: 'Ana Martínez',
        company: 'Consultora Estratégica, S.A.',
        companyInitials: 'CE',
        email: 'ana.martinez@consultora.es',
        phoneNumber: '+34 623 456 789',
        assignedAuditor: 'Javier López'
      },
      {
        id: 'client-3',
        name: 'Miguel Fernández',
        company: 'Desarrollos Tecnológicos Avanzados S.L.',
        companyInitials: 'DT',
        email: 'miguel@dta.com',
        phoneNumber: '+34 634 567 890',
        assignedAuditor: 'Elena Gómez'
      },
      {
        id: 'client-4',
        name: 'Laura Sánchez',
        company: 'Inversiones Globales S.A.',
        companyInitials: 'IG',
        email: 'laura@inversionesglobales.es',
        phoneNumber: '+34 645 678 901',
        assignedAuditor: 'Pablo García'
      },
      {
        id: 'client-5',
        name: 'Javier López',
        company: 'Comercial Distribuidora Internacional',
        companyInitials: 'CD',
        email: 'javier@cdi.com',
        phoneNumber: '+34 656 789 012',
        assignedAuditor: 'Carmen Ruiz'
      },
      {
        id: 'client-6',
        name: 'Carmen Navarro',
        company: 'Restaurantes Unidos S.L.',
        companyInitials: 'RU',
        email: 'carmen@restaurantesunidos.es',
        phoneNumber: '+34 667 890 123',
        assignedAuditor: 'Marina Torres'
      },
      {
        id: 'client-7',
        name: 'David Moreno',
        company: 'Construcciones Modernas S.A.',
        companyInitials: 'CM',
        email: 'david@construccionesmodernas.com',
        phoneNumber: '+34 678 901 234',
        assignedAuditor: 'Javier López'
      },
      {
        id: 'client-8',
        name: 'Sofía González',
        company: 'Moda Exclusiva S.L.',
        companyInitials: 'ME',
        email: 'sofia@modaexclusiva.es',
        phoneNumber: '+34 689 012 345',
        assignedAuditor: 'Elena Gómez'
      },
      {
        id: 'client-9',
        name: 'Alejandro Torres',
        company: 'Transportes Rápidos S.A.',
        companyInitials: 'TR',
        email: 'alejandro@transportesrapidos.com',
        phoneNumber: '+34 690 123 456',
        assignedAuditor: 'Pablo García'
      },
      {
        id: 'client-10',
        name: 'Isabel Herrera',
        company: 'Consultoría Integral Empresarial',
        companyInitials: 'CI',
        email: 'isabel@cie.es',
        phoneNumber: '+34 701 234 567',
        assignedAuditor: 'Carmen Ruiz'
      },
      {
        id: 'client-11',
        name: 'Pablo Vargas',
        company: 'Suministros Industriales del Norte',
        companyInitials: 'SI',
        email: 'pablo@sin.com',
        phoneNumber: '+34 712 345 678',
        assignedAuditor: 'Marina Torres'
      },
      {
        id: 'client-12',
        name: 'Lucía Ramírez',
        company: 'Gestión Hotelera Mediterránea',
        companyInitials: 'GH',
        email: 'lucia@ghm.es',
        phoneNumber: '+34 723 456 789',
        assignedAuditor: 'Javier López'
      },
      {
        id: 'client-13',
        name: 'Antonio Serrano',
        company: 'Productos Ecológicos Naturales S.L.',
        companyInitials: 'PE',
        email: 'antonio@ecologicos.com',
        phoneNumber: '+34 734 567 890',
        assignedAuditor: 'Elena Gómez'
      },
      {
        id: 'client-14',
        name: 'María Ortiz',
        company: 'Servicios Educativos Avanzados',
        companyInitials: 'SE',
        email: 'maria@educativos.es',
        phoneNumber: '+34 745 678 901',
        assignedAuditor: 'Pablo García'
      },
      {
        id: 'client-15',
        name: 'Roberto Castillo',
        company: 'Importaciones Asiáticas S.A.',
        companyInitials: 'IA',
        email: 'roberto@importaciones.com',
        phoneNumber: '+34 756 789 012',
        assignedAuditor: 'Carmen Ruiz'
      }
    ];
  }

  // Generate sample document requests with between 1-4 pending documents per client
  private generateSampleDocumentRequests(): DocumentRequest[] {
    // List of possible documents for 2024 fiscal year
    const documentTypes = [
      {
        type: 'IRPF',
        description: 'Modelo 100 - Declaración IRPF 2024'
      },
      {
        type: 'IS',
        description: 'Modelo 200 - Impuesto de Sociedades 2024'
      },
      {
        type: 'IVA Anual',
        description: 'Modelo 390 - Resumen anual IVA 2024'
      },
      {
        type: 'Operaciones Terceros',
        description: 'Modelo 347 - Declaración de operaciones con terceros 2024'
      },
      {
        type: 'Operaciones Intracomunitarias',
        description:
          'Modelo 349 - Declaración de operaciones intracomunitarias 2024'
      },
      {
        type: 'IVA Trimestral',
        description: 'Modelo 303 - Declaración trimestral IVA (4T 2024)'
      },
      {
        type: 'Retenciones',
        description: 'Modelo 111 - Retenciones e ingresos a cuenta (4T 2024)'
      },
      {
        type: 'Retenciones Arrendamiento',
        description: 'Modelo 115 - Retenciones por arrendamiento (4T 2024)'
      },
      {
        type: 'Cuentas Anuales',
        description: 'Cuentas anuales 2024'
      },
      {
        type: 'Libros Contables',
        description: 'Libros contables digitalizados 2024'
      }
    ];

    // Email distribution lists
    const emailDistributions = [
      ['documentos@auditco.es'],
      ['documentos@auditco.es', 'fiscalidad@auditco.es'],
      ['documentos@auditco.es', 'contabilidad@auditco.es'],
      ['documentos@auditco.es', 'sociedades@auditco.es'],
      ['documentos@auditco.es', 'internacional@auditco.es'],
      ['documentos@auditco.es', 'nominas@auditco.es']
    ];

    const requests: DocumentRequest[] = [];
    let docId = 1;

    // Create a function to generate a random date around March 15, 2025
    const generateDeadlineDate = (): string => {
      // Base date: March 15, 2025
      const baseDate = new Date(2025, 2, 15); // Month is 0-indexed

      // Random offset between -7 and +7 days
      const dayOffset = Math.floor(Math.random() * 15) - 7;

      const deadlineDate = new Date(baseDate);
      deadlineDate.setDate(deadlineDate.getDate() + dayOffset);

      return deadlineDate.toISOString().split('T')[0];
    };

    // For each client
    this.clients.forEach(client => {
      // How many pending docs for this client (1-4)
      const numPendingDocs = Math.floor(Math.random() * 4) + 1;

      // Select random documents without repetition
      const selectedDocIndices = new Set<number>();
      while (selectedDocIndices.size < numPendingDocs) {
        selectedDocIndices.add(
          Math.floor(Math.random() * documentTypes.length)
        );
      }

      // Create pending document requests
      Array.from(selectedDocIndices).forEach(docIndex => {
        const doc = documentTypes[docIndex];
        requests.push({
          id: `doc-${docId++}`,
          clientId: client.id,
          documentType: doc.type,
          description: doc.description,
          deadline: generateDeadlineDate(),
          status: 'pending',
          emailTo:
            emailDistributions[
              Math.floor(Math.random() * emailDistributions.length)
            ]
        });
      });

      // Add some already sent documents (0-2)
      const numSentDocs = Math.floor(Math.random() * 3);
      const sentDocIndices = new Set<number>();

      while (sentDocIndices.size < numSentDocs) {
        const randomIndex = Math.floor(Math.random() * documentTypes.length);
        // Make sure we don't duplicate with pending docs
        if (!selectedDocIndices.has(randomIndex)) {
          sentDocIndices.add(randomIndex);
        }
      }

      // Create sent document requests
      Array.from(sentDocIndices).forEach(docIndex => {
        const doc = documentTypes[docIndex];
        // Sent date is 1-10 days ago
        const sentDate = new Date();
        sentDate.setDate(
          sentDate.getDate() - (Math.floor(Math.random() * 10) + 1)
        );

        requests.push({
          id: `doc-${docId++}`,
          clientId: client.id,
          documentType: doc.type,
          description: doc.description,
          deadline: generateDeadlineDate(),
          status: 'sent',
          sentDate: sentDate.toISOString(),
          emailTo:
            emailDistributions[
              Math.floor(Math.random() * emailDistributions.length)
            ]
        });
      });
    });

    return requests;
  }
}

// Export singleton for global use
export const auditService = new AuditService();
